import React, { useState, useEffect, useRef, memo, useCallback } from "react";
import PropTypes from "prop-types";

const ManagementList = ({
  list,
  valueChanging = 0,
  amountToRender = 4,
  offset = 10,
  component: ListItem,
  itemKey,
  extraClasses = { listClasses: "", listItemClasses: "" },
  ...rest
}) => {
  const [index, setIndex] = useState(amountToRender); //base items to render;
  const listRef = useRef();
  const listToRender = list.slice(0, index);

  useEffect(() => {
    //reset index everytime valueChanging change in order to have correct index pagination
    setIndex(amountToRender);
  }, [valueChanging, amountToRender]);

  const onEndListReached = useCallback(() => {
    const itemsLeft = list.length - index;
    if (itemsLeft >= amountToRender) {
      return setIndex((index) => index + amountToRender);
    } else if (itemsLeft < amountToRender && itemsLeft > 0) {
      setIndex(list.length);
    }
  }, [list, amountToRender, index]);

  useEffect(() => {
    const listFillScreen = () => {
      const listHeight = listRef.current.getBoundingClientRect().height;
      if (!listHeight) return;
      if (listHeight <= window.innerHeight) {
        onEndListReached();
      }
    };
    listFillScreen();
  }, [onEndListReached, amountToRender, index, list.length]);

  const handleScroll = useCallback(() => {
    const endListReached =
      listRef.current.getBoundingClientRect().bottom <=
      window.innerHeight + offset;
    if (endListReached) {
      onEndListReached();
    }

    //removes listeners if initial list is smaller than amountToRender or entire list is rendered
    if (index === list.length || amountToRender >= list.length) {
      window.removeEventListener("scroll", handleScroll);
    }
  }, [onEndListReached, index, list.length, offset, amountToRender]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  const renderListContent = useCallback(
    () =>
      listToRender.map((e, i) => {
        return (
          <li
            key={`${e[itemKey]}${i}`} //itemKey is important in order to avoid weird rerendering from React side
            id="c-infinite-scroll-list-item"
            className={`u-m-none  u-p-none o-layout__item u-1/${amountToRender}  u-1/1@s ${extraClasses.listItemClasses}`}
          >
            <ListItem {...e} {...rest} />
          </li>
        );
      }),
    [listToRender, amountToRender, rest, itemKey, extraClasses.listItemClasses]
  );

  return (
    <ul
      id="c-infinite-scroll-list"
      ref={listRef}
      className={`u-p-none u-m-none o-layout ${extraClasses.listClasses}`}
    >
      {renderListContent()}
    </ul>
  );
};

ManagementList.propTypes = {
  list: PropTypes.arrayOf(PropTypes.object).isRequired,
  valueChanging: PropTypes.any,
  itemKey: PropTypes.string.isRequired,
  amountToRender: PropTypes.number,
  offset: PropTypes.number,
  component: PropTypes.elementType.isRequired,
};

export default memo(ManagementList);
